import { useLazyQuery } from '@apollo/client/react';
import {
  FilterGroupingEnum,
  SortDirectionEnum,
  User,
  UserFilterInputObject,
  UserScopeInputObject,
  UserScopeNameEnum,
  UsersSearchListQuery,
  UsersSearchListQueryVariables,
} from 'graphql-common';
import { DEFAULT_SEARCH_LIST_VARIABLES } from '@lib/enums/urls';
import getFullName from '@lib/utils/getFullName';
import getLoadOptionsResult from '@lib/utils/getLoadOptionsResult';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { LoadOptionsParams } from '@lib/components/Select/types';

type FetchLazyQuery = useLazyQuery.ExecFunction<
  UsersSearchListQuery,
  UsersSearchListQueryVariables
>;

export const fetchSearchList = async (
  searchQuery: string,
  fetchLazyQuery: FetchLazyQuery,
  page: number,
  scope?: UserScopeInputObject,
  additionalFilters?: object[],
) => {
  const options: { variables: UsersSearchListQueryVariables } = {
    variables: {
      ...DEFAULT_SEARCH_LIST_VARIABLES,
      page,
      sorts: { fullName: SortDirectionEnum.Asc },
      scope,
    },
  };
  const filters: UserFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'fullName' },
        { fieldName: 'email' },
      ]),
    );
  }
  if (additionalFilters?.length) {
    filters.push(...additionalFilters);
  }
  if (filters.length) {
    options.variables.filters = filters;
  }
  const { data } = await fetchLazyQuery(options);
  return getLoadOptionsResult(
    data,
    (dataItem: User) => ({
      value: dataItem.id,
      label: getFullName(dataItem),
      data: dataItem,
    }),
    searchQuery,
  );
};

export const getUsersLoadOptions =
  (
    fetchLazyQuery: FetchLazyQuery,
    scope?: UserScopeInputObject,
    additionalFilters?: object[],
  ) =>
  (searchQuery: string, loadedOptions: unknown, params: LoadOptionsParams) =>
    fetchSearchList(
      searchQuery,
      fetchLazyQuery,
      params?.page || 1,
      scope,
      additionalFilters,
    );

// Users who can receive a generated file by email: active and with an email
export const getEmailRecipientsLoadOptions = (fetchLazyQuery: FetchLazyQuery) =>
  getUsersLoadOptions(fetchLazyQuery, { name: UserScopeNameEnum.Active }, [
    {
      email: {
        grouping: FilterGroupingEnum.Or,
        predicate: { null: false },
        filterGroup: 'email',
      },
    },
  ]);
